/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    connectedCallback() {
      this.canvas = this.querySelector('.hotspots-image__canvas');
      this.fitEl = this.querySelector('.hotspots-image__fit');
      this.img = this.canvas?.querySelector('img');
      this.textEl = this.closest('.shoppable-image')?.querySelector('.shoppable-image__text');

      if (!this.canvas || !this.img) return;

      this.syncLayoutHandler = this.syncLayoutHandler || this.syncLayout.bind(this);
      this.layoutResizeHandler = this.layoutResizeHandler || this.handleLayoutResize.bind(this);

      this.img.addEventListener('load', this.syncLayoutHandler);

      if (this.img.complete) {
        this.syncLayout();
      }

      if ('ResizeObserver' in window) {
        this.layoutObserver = new ResizeObserver(this.syncLayoutHandler);
        this.layoutObserver.observe(this.canvas);

        if (this.textEl) {
          this.layoutObserver.observe(this.textEl);
        }
      }

      window.addEventListener('on:debounced-resize', this.layoutResizeHandler);
    }

    disconnectedCallback() {
      if (this.img && this.syncLayoutHandler) {
        this.img.removeEventListener('load', this.syncLayoutHandler);
      }

      this.layoutObserver?.disconnect();
      window.removeEventListener('on:debounced-resize', this.layoutResizeHandler);
    }

    handleLayoutResize() {
      this.syncLayout();

      if (this.activeHotspot) {
        this.closeActiveHotspot({ type: 'on:debounced-resize' });
      }
    }

    usesFillLayout() {
      return Boolean(this.closest('.shoppable-image')) && window.matchMedia('(min-width: 1024px)').matches;
    }

    syncLayout() {
      this.canvas.querySelectorAll('.hotspot').forEach((hotspot) => {
        hotspot.style.left = `${hotspot.dataset.x}%`;
        hotspot.style.top = `${hotspot.dataset.y}%`;
      });

      this.canvas.style.transform = '';

      if (this.fitEl) {
        this.fitEl.style.height = '';
      }

      if (!this.usesFillLayout() || !this.fitEl || !this.textEl) return;

      const targetHeight = this.textEl.offsetHeight;
      const canvasWidth = this.canvas.offsetWidth;
      const { naturalWidth, naturalHeight } = this.img;

      if (!targetHeight || !canvasWidth || !naturalWidth || !naturalHeight) return;

      const naturalCanvasHeight = canvasWidth * (naturalHeight / naturalWidth);
      this.fitEl.style.height = `${targetHeight}px`;

      if (targetHeight > naturalCanvasHeight) {
        const scale = targetHeight / naturalCanvasHeight;
        this.canvas.style.transform = `scale(${scale})`;
      }
    }

    /**
     * Handles 'click' events on the hotspot images.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.hotspot__btn')) return;
      const hotspot = evt.target.parentNode;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
      } else {
        if (this.activeHotspot) this.closeActiveHotspot(evt);
        this.activateHotspot(hotspot);
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleClickOutside(evt) {
      if (evt.target.matches('.hotspot__btn') || evt.target.matches('.hotspot__card')) return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles 'keyup' events on the document.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Returns the card that belongs to a hotspot. Once a card has been portalled
     * out of the (clipped) image wrapper we keep a reference on the hotspot, so
     * it can still be found even though it no longer lives inside the hotspot.
     * @param {Element} hotspot - Hotspot element.
     * @returns {Element} The hotspot's card.
     */
    getCard(hotspot) {
      return hotspot.hotspotCard || hotspot.querySelector('.hotspot__card');
    }

    /**
     * Activates a hotspot and shows the corresponding product card.
     * @param {Element} hotspot - Selected hotspot.
     */
    activateHotspot(hotspot) {
      this.activeCard = this.getCard(hotspot);
      this.portalCard(hotspot);
      this.setHotspotActiveState(hotspot, true);

      // Reveal on the next frame so the open transition plays after the move.
      requestAnimationFrame(() => {
        if (this.activeCard) this.activeCard.classList.add('is-open');
      });

      trapFocus(this.activeCard);

      this.clickOutsideHandler = this.clickOutsideHandler || this.handleClickOutside.bind(this);
      this.keyUpHandler = this.keyUpHandler || this.handleKeyup.bind(this);

      document.addEventListener('click', this.clickOutsideHandler);
      document.addEventListener('keyup', this.keyUpHandler);
    }

    /**
     * Hides the product card and deactivates the hotspot.
     * @param {object} evt - Event object.
     */
    closeActiveHotspot(evt) {
      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);

      if (!this.activeHotspot) return;

      // The card just fades out (via CSS) and stays hidden in its host; there is
      // nothing to move back, so no transitionend bookkeeping is needed.
      if (this.activeCard) this.activeCard.classList.remove('is-open');
      this.setHotspotActiveState(this.activeHotspot, false);
      removeTrapFocus();
      this.activeCard = null;
    }

    /**
     * Moves the active card out of the (clipped) image wrapper into the section
     * container so it can overlap neighbouring content, then positions it. The
     * card is only moved once; afterwards it is simply repositioned and shown.
     * @param {Element} hotspot - Selected hotspot.
     */
    portalCard(hotspot) {
      const card = this.activeCard;
      const host = this.closest('.container') || this;

      if (getComputedStyle(host).position === 'static') {
        host.style.position = 'relative';
      }

      if (card.parentNode !== host) {
        hotspot.hotspotCard = card;
        host.appendChild(card);
      }

      this.positionCard(hotspot.querySelector('.hotspot__btn'), host, card);
    }

    /**
     * Positions the card just below its hotspot button, keeping it within the
     * viewport (flipping above / nudging sideways when it would overflow).
     * @param {Element} button - The hotspot button the card belongs to.
     * @param {Element} host - The element the card is positioned against.
     * @param {Element} card - The card to position.
     */
    positionCard(button, host, card) {
      card.style.top = '';
      card.style.bottom = '';
      card.style.left = '';

      const hostRect = host.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const margin = 20;
      // offsetWidth/Height ignore the transform scale, so they give the real size.
      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;

      const centerX = buttonRect.left + buttonRect.width / 2 - hostRect.left;
      let top = buttonRect.bottom - hostRect.top + 12;

      // Flip above the button if the card would fall out of the viewport bottom.
      if (hostRect.top + top + cardHeight > document.documentElement.clientHeight) {
        top = buttonRect.top - hostRect.top - cardHeight - 12;
      }

      // The card is centred on `left` via translateX(-50%); nudge it so it stays
      // within the viewport horizontally.
      let left = centerX;
      const viewportLeft = hostRect.left + centerX - cardWidth / 2;
      const viewportRight = viewportLeft + cardWidth;

      if (viewportRight > window.innerWidth - margin) {
        left -= viewportRight - (window.innerWidth - margin);
      } else if (viewportLeft < margin) {
        left += margin - viewportLeft;
      }

      card.style.top = `${top}px`;
      card.style.left = `${left}px`;
    }

    /**
     * Sets the active state of a hotspot.
     * @param {Element} hotspot - Hotspot element.
     * @param {boolean} active - Set the hotspot as active.
     */
    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);
      hotspot.querySelector('.hotspot__btn').setAttribute('aria-expanded', active);
      if (this.activeCard) this.activeCard.setAttribute('aria-hidden', !active);

      this.activeHotspot = active ? hotspot : null;
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}
